/* Shared layout for SEO articles: Quick Answer box, last-reviewed date,
   prose body, CTA to the calculator, cross-links. */
import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import useSEO from "@/lib/useSEO";
import QuickAnswer from "@/components/QuickAnswer";

export default function ArticleLayout({ meta, children }) {
  useSEO({
    title: meta.title,
    description: meta.seoDescription,
    canonicalPath: `/learn/${meta.slug}`,
  });
  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      <div className="max-w-3xl mx-auto px-6 py-10">
        <Link
          to="/learn"
          className="inline-flex items-center gap-1.5 text-sm text-[#5C6B62] hover:text-[#2B5339] mb-8"
        >
          <ArrowLeft size={14} /> All guides
        </Link>
        <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#2B5339] mb-2">
          {meta.hub} · {meta.time}
        </div>
        <h1 className="text-4xl sm:text-5xl font-medium text-[#1C2B23] leading-tight">
          {meta.h1}
        </h1>
        <p className="mt-4 text-lg text-[#5C6B62] leading-relaxed max-w-2xl">{meta.hook}</p>

        <QuickAnswer reviewed={meta.reviewed || "June 2026"}>{meta.quickAnswer}</QuickAnswer>

        <div className="mt-10 space-y-4 text-[#1C2B23] leading-relaxed text-[15px]">
          {children}
        </div>

        <div className="mt-12 bg-[#1C2B23] text-white rounded-2xl p-6">
          <div className="text-[10px] tracking-[0.2em] uppercase font-semibold text-[#9BB8A5] mb-2">
            Run your own numbers
          </div>
          <p className="text-sm leading-relaxed mb-4">
            Every article on this site connects to the free Centennial calculator — 5,000 Monte
            Carlo simulations to age 100, live market data, no signup.
          </p>
          <Link
            data-testid="article-cta-calculator"
            to="/"
            className="inline-block bg-white text-[#1C2B23] rounded-full px-5 py-2.5 text-sm font-medium hover:bg-[#E8F0EB]"
          >
            Run my free retirement plan →
          </Link>
        </div>

        <div className="mt-8 text-xs text-[#5C6B62]">
          Written and reviewed by the Centennial editorial team, founded by{" "}
          <Link to="/about" className="text-[#2B5339] underline">Hank Beach</Link>. Educational
          only — see our <Link to="/disclaimer" className="text-[#2B5339] underline">disclaimer</Link> and{" "}
          <Link to="/methodology" className="text-[#2B5339] underline">methodology</Link>.
        </div>
      </div>
    </div>
  );
}

export const H2 = ({ children }) => (
  <h2 className="text-2xl font-medium mt-10 mb-1">{children}</h2>
);

export const DataTable = ({ head, rows }) => (
  <div className="overflow-x-auto my-4">
    <table className="w-full text-sm border border-[#E5E2DC] rounded-lg overflow-hidden">
      <thead>
        <tr className="bg-[#F0EFEA] text-left">
          {head.map((h) => (
            <th key={h} className="px-3 py-2 font-medium text-[#1C2B23]">{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-[#E5E2DC] bg-white">
            {r.map((c, j) => (
              <td key={j} className="px-3 py-2 text-[#1C2B23]">{c}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);
